// Bots (DESIGN §4). A bot fills a seat exactly like a person: each step it reads what that seat's client would read —
// the public view, its own private view, the public events and its own private events — and answers with the same
// input a person sends (stick, sprint, ACTION; POP with a target id + aim point; WHISTLE). It never touches the sim.
// That keeps the bot Inspector honest: it can only suspect a body from the tells everyone can see.
import * as T from "./tuning";
import { BOT, BOT_EXTRA, HUMAN_MODEL, type Call } from "./tuning";
import { makeRng, range, normal, pick, weighted, type Rng } from "./rng";
import * as M from "./map";
import type { PublicView, PrivateView, PublicBody, SimEvent, SeatInput, Vec3, Anim } from "./sim";

/** what a bot does this step: the same three things a client can send */
export type BotOut = { input: SeatInput; pops: { id: number; aim: Vec3 }[]; whistle: boolean };
export interface BotBrain { readonly role: "sneak" | "inspector"; update(pub: PublicView, priv: PrivateView, events: SimEvent[]): BotOut; }

const still = (): SeatInput => ({ mx: 0, mz: 0, sprint: false, act: false });

/** path-following stick: walks the nav grid toward a goal; replans when the goal moves or progress stalls */
class Walker {
  goal: M.V2 | null = null; private path: M.V2[] = []; private pi = 0; private stuckAt = 0; private sx = 0; private sz = 0;
  go(x: number, z: number, fx: number, fz: number, now: number) {
    if (this.goal && Math.hypot(this.goal.x - x, this.goal.z - z) < 0.4 && this.path.length) return;
    this.goal = { x, z }; this.path = M.findPath(fx, fz, x, z) || []; this.pi = 0; this.stuckAt = now; this.sx = fx; this.sz = fz;
  }
  clear() { this.goal = null; this.path = []; }
  /** the stick toward the next waypoint; null once within `tol` of the goal */
  stick(x: number, z: number, now: number, tol: number): { mx: number; mz: number } | null {
    if (!this.goal) return null;
    if (Math.hypot(this.goal.x - x, this.goal.z - z) <= tol) return null;
    let wp = this.path[this.pi];
    while (wp && Math.hypot(wp.x - x, wp.z - z) < T.ARRIVE_R) { this.pi++; wp = this.path[this.pi]; }
    if (!wp) wp = this.goal;
    if (now - this.stuckAt > T.STUCK_SEC) {
      if (Math.hypot(x - this.sx, z - this.sz) < 0.4) { this.path = M.findPath(x, z, this.goal.x, this.goal.z) || []; this.pi = 0; }
      this.stuckAt = now; this.sx = x; this.sz = z;
    }
    const dx = wp.x - x, dz = wp.z - z, d = Math.hypot(dx, dz) || 1;
    return { mx: dx / d, mz: dz / d };
  }
}

const angDiff = (a: number, b: number) => { let d = a - b; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI; return Math.abs(d); };
/** does the Inspector at body i see point p? (radius + view cone toward its heading + line of sight) */
export function inspectorSees(i: PublicBody, x: number, z: number, radius = BOT.inspPerception, coneDeg = BOT.inspConeDeg): boolean {
  const d = Math.hypot(x - i.x, z - i.z);
  if (d > radius) return false;
  if (d > 0.8 && angDiff(Math.atan2(x - i.x, z - i.z), i.h) > (coneDeg * Math.PI) / 360) return false;
  return M.losClear(i.x, i.z, x, z);
}
const inPanic = (pub: PublicView, x: number, z: number) => pub.panics.some((p) => Math.hypot(p.x - x, p.z - z) <= T.PANIC_R + 0.5);

// =====================================================================================================================
// The bot Sneak

export type SneakParams = { react: number; reactSd: number; miss: number; fallsForWhistle: number; sprintFar: number; sprintNeverWithin: number; wary: number; coverMin: number; coverMax: number; firstDelayMax: number; noticeMin: number; noticeMax: number };
export const BOT_SNEAK: SneakParams = { react: BOT.sneakReact, reactSd: BOT.sneakReactSd, miss: BOT.sneakMiss, fallsForWhistle: BOT.sneakFallsForWhistle, sprintFar: BOT.sneakSprintIfInspectorFarther, sprintNeverWithin: 0, wary: BOT_EXTRA.sneakWary, coverMin: BOT_EXTRA.sneakCoverMin, coverMax: BOT_EXTRA.sneakCoverMax, firstDelayMax: BOT_EXTRA.sneakFirstDelayMax, noticeMin: BOT_EXTRA.sneakNoticeMin, noticeMax: BOT_EXTRA.sneakNoticeMax };
/** the person-like Sneak of the human-model gate: slower, misses more, never sprints anywhere near the Inspector */
export const HUMAN_SNEAK: SneakParams = { ...BOT_SNEAK, react: HUMAN_MODEL.react, reactSd: HUMAN_MODEL.reactSd, miss: HUMAN_MODEL.miss, fallsForWhistle: HUMAN_MODEL.fallsForWhistle, sprintNeverWithin: HUMAN_MODEL.sprintNeverWithin, noticeMin: HUMAN_MODEL.noticeMin, noticeMax: HUMAN_MODEL.noticeMax };

type SneakState = "cover" | "toStall" | "grab" | "toDrain" | "stash";

export class BotSneak implements BotBrain {
  readonly role = "sneak" as const;
  private r: Rng; private w = new Walker();
  private state: SneakState = "cover"; private coverUntil = 0; private stall = -1; private golden = false; private drain = -1;
  private waryRoll = false; private waryUntil = 0; private jobAt = 0; private coverGoal: M.V2 | null = null;
  private press = -1; private pressKind: Call | null = null; private stillUntil = 0;
  private round = -1; private me = 0;
  /** when we notice each stall's capsule (it popped onto the stall at some moment; we see it a little later) */
  private noticeAt: number[] = []; private had: boolean[] = [];
  constructor(seed: number, public p: SneakParams = BOT_SNEAK) { this.r = makeRng(seed); }

  update(pub: PublicView, priv: PrivateView, events: SimEvent[]): BotOut {
    const out: BotOut = { input: still(), pops: [], whistle: false }, now = pub.time, r = this.r, p = this.p;
    if (pub.round !== this.round) { this.round = pub.round; this.reset(now, pub.phase === "intro" ? pub.phaseLeft + range(r, 0, p.firstDelayMax) : range(r, 0, p.firstDelayMax)); }
    pub.stalls.forEach((h, i) => { if (h && !this.had[i]) this.noticeAt[i] = now + range(r, p.noticeMin, p.noticeMax); this.had[i] = h; });
    if (!priv.me) { this.press = -1; return out; }
    if (priv.me !== this.me) { this.me = priv.me; this.reset(now, range(r, 1, 4)); }
    // ---- calls and whistles: a reaction time, sometimes a miss; a whistle sometimes fools us
    for (const e of events) {
      if (e.k === "call") { if (r() >= p.miss) { this.press = now + Math.max(0.08, normal(r, p.react, p.reactSd)); this.pressKind = e.call; } }
      else if (e.k === "whistle") { if (r() < p.fallsForWhistle) { this.press = now + Math.max(0.08, normal(r, p.react, p.reactSd)); this.pressKind = e.call; } }
    }
    if (pub.phase !== "play") return out;
    if (priv.locked) return out;   // performing a call (FREEZE: we keep the stick still too)
    if (this.press >= 0) {
      if (now >= this.press) {
        if (this.pressKind === "freeze") this.stillUntil = now + T.CALL_HOLD + 0.3;
        else out.input.act = true;   // the press edge (we let go of ACTION while waiting so this is an edge)
        this.press = -1;
        return out;
      }
      out.input.act = false;
    }
    if (now < this.stillUntil) return out;
    const me = { x: priv.x, z: priv.z };
    const insp = pub.bodies.find((b) => b.id === pub.inspector) || null;
    const inspD = insp ? Math.hypot(insp.x - me.x, insp.z - me.z) : 99;
    const seen = (x: number, z: number) => !!insp && inspectorSees(insp, x, z);
    const holding = this.press < 0;   // don't hold ACTION while a call press is pending
    // ---- the job
    if (this.state === "cover") {
      if (now >= this.coverUntil) this.pickJob(pub, priv, insp, now);
      else {
        if (this.coverGoal && !this.w.goal) { this.w.go(this.coverGoal.x, this.coverGoal.z, me.x, me.z, now); this.coverGoal = null; }
        const s = this.w.stick(me.x, me.z, now, 0.4);
        if (s) { out.input.mx = s.mx; out.input.mz = s.mz; }
        return out;
      }
    }
    if (this.state === "toStall") {
      if (priv.carry > 0) { this.toDrain(pub, priv, insp, now); return out; }
      if (this.golden) { if (pub.golden !== 1 || !pub.float) { this.cover(now, 1, 3); return out; } this.w.go(pub.float.x, pub.float.z, me.x, me.z, now); }
      else if (!pub.stalls[this.stall]) { this.pickJob(pub, priv, insp, now); return out; }
      const tol = this.golden ? T.GOLDEN_GRAB_RANGE - 0.6 : 0.3;
      const s = this.w.stick(me.x, me.z, now, tol);
      const inRange = this.golden ? !!pub.float && Math.hypot(pub.float.x - me.x, pub.float.z - me.z) <= T.GOLDEN_GRAB_RANGE - 0.3 : M.stallNear(me.x, me.z, T.GRAB_RANGE - 0.15) === this.stall;
      if (s && !inRange) { out.input.mx = s.mx; out.input.mz = s.mz; out.input.sprint = this.sprintOk(pub, me, inspD); return out; }
      // at the counter: wait out the Inspector's gaze if he is looking right at us (sometimes)
      if (this.lingering(seen(me.x, me.z), now)) return out;
      this.state = "grab"; this.jobAt = now;
    }
    if (this.state === "grab") {
      if (priv.carry > 0) { this.toDrain(pub, priv, insp, now); return out; }
      if ((!this.golden && !pub.stalls[this.stall]) || (this.golden && pub.golden !== 1) || now - this.jobAt > 4) { this.pickJob(pub, priv, insp, now); return out; }
      out.input.act = holding;
      return out;
    }
    if (this.state === "toDrain") {
      if (priv.carry === 0) { this.cover(now, p.coverMin, p.coverMax); return out; }
      const d = M.DRAINS[this.drain];
      const s = this.w.stick(me.x, me.z, now, 0.25);
      if (s && Math.hypot(d.x - me.x, d.z - me.z) > T.STASH_RANGE - 0.3) { out.input.mx = s.mx; out.input.mz = s.mz; out.input.sprint = this.sprintOk(pub, me, inspD); return out; }
      if (this.lingering(seen(me.x, me.z), now)) return out;
      this.state = "stash"; this.jobAt = now;
    }
    if (this.state === "stash") {
      if (priv.carry === 0) { this.cover(now, p.coverMin, p.coverMax); return out; }
      if (now - this.jobAt > 4) { this.toDrain(pub, priv, insp, now); return out; }
      out.input.act = holding;
    }
    return out;
  }

  private reset(now: number, delay: number) { this.state = "cover"; this.press = -1; this.stillUntil = 0; this.coverUntil = now + delay; this.wander(now); }
  private cover(now: number, lo: number, hi: number) { this.state = "cover"; this.coverUntil = now + range(this.r, lo, hi); this.wander(now); }
  /** look normal: amble to an open spot and stand about (the walk starts from wherever we are next step) */
  private wander(_now: number) { const p = pick(this.r, M.POIS.filter((q) => q.kind === "plaza" || q.kind === "rim")); this.w.clear(); this.coverGoal = { x: p.x + range(this.r, -0.6, 0.6), z: p.z + range(this.r, -0.6, 0.6) }; }

  /** pick a capsule: near me, far from the Inspector (DESIGN §4) */
  private pickJob(pub: PublicView, priv: PrivateView, insp: PublicBody | null, now: number) {
    if (priv.carry > 0) { this.toDrain(pub, priv, insp, now); return; }
    const opts: { stall: number; golden: boolean; x: number; z: number; score: number }[] = [];
    const score = (x: number, z: number) => Math.hypot(x - priv.x, z - priv.z) + (insp ? 14 * Math.max(0, 1 - Math.hypot(insp.x - x, insp.z - z) / BOT.inspPerception) : 0) + range(this.r, 0, 6);
    for (const s of M.STALLS) if (pub.stalls[s.i] && now >= (this.noticeAt[s.i] ?? 0)) { const spot = s.spots.reduce((a, b) => (Math.hypot(a.x - priv.x, a.z - priv.z) < Math.hypot(b.x - priv.x, b.z - priv.z) ? a : b)); opts.push({ stall: s.i, golden: false, x: spot.x, z: spot.z, score: score(spot.x, spot.z) }); }
    if (pub.golden === 1 && pub.float) opts.push({ stall: -1, golden: true, x: pub.float.x, z: pub.float.z, score: score(pub.float.x, pub.float.z) - 4 });
    if (!opts.length) { this.cover(now, 2, 5); return; }
    opts.sort((a, b) => a.score - b.score);
    const o = opts[0];
    this.state = "toStall"; this.stall = o.stall; this.golden = o.golden;
    this.w.clear(); this.w.go(o.x, o.z, priv.x, priv.z, now);
    this.armWary();
  }
  /** roll whether we'll wait out the Inspector's gaze when we reach the counter / the drain */
  private armWary() { this.waryRoll = this.r() < this.p.wary; this.waryUntil = 0; }
  /** at the counter / drain: if he is looking right at us, linger a moment (and then go ahead anyway) */
  private lingering(seen: boolean, now: number) {
    if (!this.waryRoll || !seen) return false;
    if (this.waryUntil === 0) this.waryUntil = now + range(this.r, 1.5, 4);
    return now < this.waryUntil;
  }
  /** the drain farthest from the Inspector's gaze (DESIGN §4), not absurdly far from me */
  private toDrain(pub: PublicView, priv: PrivateView, insp: PublicBody | null, now: number) {
    let best = 0, bs = Infinity;
    for (const d of M.DRAINS) {
      const s = Math.hypot(d.x - priv.x, d.z - priv.z) * 0.8 - (insp ? Math.hypot(d.x - insp.x, d.z - insp.z) * 0.6 + (inspectorSees(insp, d.x, d.z) ? -10 : 0) : 0) + range(this.r, 0, 3);
      if (s < bs) { bs = s; best = d.i; }
    }
    this.drain = best; this.state = "toDrain";
    const d = M.DRAINS[best], a = Math.atan2(priv.x - d.x, priv.z - d.z);
    const at = M.nearestFree(d.x + Math.sin(a) * 0.55, d.z + Math.cos(a) * 0.55);
    this.w.clear(); this.w.go(at.x, at.z, priv.x, priv.z, now);
    this.armWary();
  }
  private sprintOk(pub: PublicView, me: M.V2, inspD: number) {
    if (this.p.sprintNeverWithin && inspD < this.p.sprintNeverWithin) return false;
    return inspD > this.p.sprintFar || inPanic(pub, me.x, me.z);
  }
}

// =====================================================================================================================
// The bot Inspector

export class BotInspector implements BotBrain {
  readonly role = "inspector" as const;
  private r: Rng; private w = new Walker();
  private sus = new Map<number, number>(); private lastAnim = new Map<number, Anim>();
  private pending: { id: number; at: number } | null = null;
  private nextWhistle = 0; private lookUntil = 0; private lookDir = 0; private lastT = -1; private round = -1; private thinkAt = 0;
  private seenNow = new Set<number>();
  constructor(seed: number) { this.r = makeRng(seed); }

  update(pub: PublicView, priv: PrivateView, events: SimEvent[]): BotOut {
    const out: BotOut = { input: still(), pops: [], whistle: false }, now = pub.time, r = this.r;
    if (pub.round !== this.round) { this.round = pub.round; this.sus.clear(); this.lastAnim.clear(); this.pending = null; this.nextWhistle = now + (pub.phase === "intro" ? pub.phaseLeft : 0) + range(r, BOT.inspWhistleMin, BOT.inspWhistleMax) * 0.5; this.w.clear(); this.lastT = now; }
    const me = pub.bodies.find((b) => b.id === priv.me);
    if (!me || pub.phase !== "play") { this.lastT = now; return out; }
    const dt = Math.max(0, now - this.lastT); this.lastT = now;
    const gentle = pub.onboarding || priv.gentle;
    const byId = new Map(pub.bodies.map((b) => [b.id, b]));
    // ---- perception (radius + view cone toward my heading + line of sight), refreshed at inspThinkHz
    if (now >= this.thinkAt) {
      this.thinkAt = now + 1 / BOT_EXTRA.inspThinkHz;
      this.seenNow.clear();
      for (const b of pub.bodies) if (b.id !== me.id && !b.revealed && inspectorSees(me, b.x, b.z)) this.seenNow.add(b.id);
    }
    const seen = (id: number) => this.seenNow.has(id);
    // ---- suspicion from tells (DESIGN §4): only what anyone watching could have seen
    for (const [id, s] of this.sus) { const n = s - BOT.susDecayPerSec * dt; if (n <= 0 || !byId.has(id)) this.sus.delete(id); else this.sus.set(id, n); }
    const add = (id: number, v: number) => this.sus.set(id, (this.sus.get(id) || 0) + v);
    for (const e of events) {
      if (e.k === "blunder" && seen(e.id)) add(e.id, BOT.susBlunder);
      else if (e.k === "glint" && seen(e.id)) add(e.id, BOT.susGlint);
      else if (e.k === "grab") {
        for (const id of this.seenNow) {
          const b = byId.get(id); if (!b || b.anim !== "browse") continue;
          const near = e.stall >= 0 ? M.stallNear(b.x, b.z, T.GRAB_RANGE + 0.3) === e.stall : !!pub.float && Math.hypot(b.x - pub.float.x, b.z - pub.float.z) <= T.GOLDEN_GRAB_RANGE + 0.3;
          if (near) add(id, BOT.susGrab);
        }
      }
    }
    for (const id of this.seenNow) {
      const b = byId.get(id); if (!b) continue;
      if (b.anim === "run" && !inPanic(pub, b.x, b.z)) add(id, BOT.susSprintPerSec * dt);
      const was = this.lastAnim.get(id);
      if (b.anim === "shoelace" && was !== "shoelace" && M.drainNear(b.x, b.z, T.STASH_RANGE + 0.4) >= 0) add(id, BOT.susStash);
    }
    for (const b of pub.bodies) this.lastAnim.set(b.id, b.anim);
    // ---- the top suspect
    let top: PublicBody | null = null, ts = 0;
    for (const [id, s] of this.sus) { const b = byId.get(id); if (b && !b.revealed && s > ts) { ts = s; top = b; } }
    const threshold = gentle ? BOT.inspThresholdGentle : BOT.inspThreshold;
    const canPop = priv.charges > 0 && (!gentle || pub.playT >= T.ONBOARD_SAFE);
    const reach = (b: PublicBody) => Math.hypot(b.x - me.x, b.z - me.z) <= T.POP_RANGE - 0.5 && M.losClear(me.x, me.z, b.x, b.z);
    if (!this.pending && top && ts >= threshold && canPop && reach(top)) {
      const react = range(r, BOT.inspReactMin, BOT.inspReactMax) * (gentle ? BOT_EXTRA.inspGentleReactMult : 1);
      this.pending = { id: top.id, at: now + react };
    }
    if (this.pending && now >= this.pending.at && priv.cooldown <= 0) {
      const b = byId.get(this.pending.id);
      if (b && !b.revealed && reach(b) && priv.charges > 0) {
        const aim = { x: b.x + normal(r, 0, BOT.inspAimErr / 2), y: 0.8, z: b.z + normal(r, 0, BOT.inspAimErr / 2) };
        out.pops.push({ id: b.id, aim });
        this.sus.delete(b.id);
      }
      this.pending = null;
    }
    // ---- the fake call: when a crowd is in view (never in gentle mode)
    if (!gentle && now >= this.nextWhistle && priv.whistleIn <= 0 && !pub.call && this.seenNow.size >= BOT_EXTRA.inspWhistleCrowd) {
      out.whistle = true; this.nextWhistle = now + range(r, BOT.inspWhistleMin, BOT.inspWhistleMax);
    }
    // ---- where to walk and where to look
    let goal: M.V2 | null = null, faceTo: M.V2 | null = null;
    if (priv.scriptGoal) goal = priv.scriptGoal;
    else if (top && ts >= BOT_EXTRA.inspChaseScore) {
      faceTo = top;
      if (Math.hypot(top.x - me.x, top.z - me.z) > BOT_EXTRA.inspStandoff || !M.losClear(me.x, me.z, top.x, top.z)) goal = top;
    } else {
      if (!this.w.goal || now < this.lookUntil) { /* looking around at a patrol point */ }
      if (!this.w.goal && now >= this.lookUntil) { const p = this.patrolPoint(pub); this.w.go(p.x, p.z, me.x, me.z, now); }
      if (this.w.goal) {
        const s = this.w.stick(me.x, me.z, now, 0.6);
        if (s) { out.input.mx = s.mx; out.input.mz = s.mz; return out; }
        this.w.clear(); this.lookUntil = now + BOT_EXTRA.inspPatrolLook; this.lookDir = me.h;
      }
      // sweep the gaze while standing: a tap of the stick turns the body
      this.lookDir += dt * 1.2;
      faceTo = { x: me.x + Math.sin(this.lookDir) * 5, z: me.z + Math.cos(this.lookDir) * 5 };
    }
    if (goal) {
      this.w.go(goal.x, goal.z, me.x, me.z, now);
      const s = this.w.stick(me.x, me.z, now, 0.6);
      if (s) { out.input.mx = s.mx; out.input.mz = s.mz; return out; }
    }
    if (faceTo) {
      const want = Math.atan2(faceTo.x - me.x, faceTo.z - me.z);
      if (angDiff(want, me.h) > 0.35) { out.input.mx = Math.sin(want); out.input.mz = Math.cos(want); }
    }
    return out;
  }
  /** patrol where the crime happens: a few metres in front of a stall that holds a capsule (watching who browses it),
   *  by a drain, now and then the open plaza */
  private patrolPoint(pub: PublicView): M.V2 {
    const r = this.r;
    const stocked = M.STALLS.filter((s) => pub.stalls[s.i]);
    const k = weighted(r, [stocked.length ? BOT_EXTRA.inspWatchStall : 0, BOT_EXTRA.inspWatchDrain, 1]);
    if (k === 0) { const s = pick(r, stocked); const p = M.toWorld(s.x, s.z, s.rot, range(r, -2, 2), s.d / 2 + range(r, 4, 7)); return M.nearestFree(p.x, p.z); }
    const pool = M.POIS.filter((p) => (k === 1 ? p.kind === "drain" : p.kind === "plaza"));
    const p = pick(r, pool);
    return M.nearestFree(p.x + range(r, -2, 2), p.z + range(r, -2, 2));
  }
}
